import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Star, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function StarRating({ rating, onRate, interactive = false }) {
  return (
    <div className="flex gap-1">
      {[1, 2, 3, 4, 5].map((s) => (
        <Star
          key={s}
          size={interactive ? 24 : 16}
          className={`${
            s <= rating ? "fill-gg-red text-gg-red" : "text-zinc-600"
          } ${interactive ? "cursor-pointer hover:text-gg-red transition-colors" : ""}`}
          onClick={() => interactive && onRate(s)}
        />
      ))}
    </div>
  );
}

export default function Reviews() {
  const [reviews, setReviews] = useState([]);
  const [name, setName] = useState("");
  const [text, setText] = useState("");
  const [rating, setRating] = useState(5);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    axios.get(`${API}/reviews`).then((r) => setReviews(r.data)).catch(() => {});
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !text.trim()) {
      toast.error("Please fill in all fields");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/reviews`, { name: name.trim(), text: text.trim(), rating });
      toast.success("Review submitted! It will appear after approval.");
      setName("");
      setText("");
      setRating(5);
    } catch {
      toast.error("Failed to submit review");
    }
    setSubmitting(false);
  };

  return (
    <section id="reviews" className="py-8 md:py-10 bg-gg-surface" data-testid="reviews-section">
      <div className="max-w-7xl mx-auto px-4 md:px-6">

        {/* Reviews Masonry Grid */}
        {reviews.length > 0 && (
          <div className="columns-1 md:columns-2 lg:columns-3 gap-4 mt-8 space-y-4">
            {reviews.map((r, i) => (
              <motion.div
                key={r.id || i}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: i * 0.08 }}
                className="break-inside-avoid border border-white/10 p-6 bg-gg-black/50 rounded-xl hover:border-gg-red/30 transition-all duration-300 group"
                data-testid={`review-card-${i}`}
              >
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-full bg-gg-red/10 flex items-center justify-center shrink-0">
                      <span className="text-gg-red text-sm font-bold">{r.name?.[0]?.toUpperCase()}</span>
                    </div>
                    <span className="text-white font-heading uppercase tracking-widest text-[10px]">{r.name}</span>
                  </div>
                  <StarRating rating={r.rating} />
                </div>
                <p className="text-zinc-300 text-sm leading-relaxed italic">"{r.text}"</p>
              </motion.div>
            ))}
          </div>
        )}

        {reviews.length === 0 && (
          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-zinc-500 mt-8 text-sm"
          >
            No reviews yet. Be the first to share your experience!
          </motion.p>
        )}

        {/* Submit Review Form */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.2 }}
          className="mt-16 max-w-lg"
        >
          <h3 className="font-heading text-xl uppercase tracking-wider font-bold text-white mb-6">
            Share Your Experience
          </h3>
          <form onSubmit={handleSubmit} className="space-y-4" data-testid="review-form">
            <Input
              placeholder="Your Name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="bg-gg-black border-white/10 text-white placeholder:text-zinc-600 rounded-lg"
              data-testid="review-name-input"
            />
            <div>
              <span className="text-zinc-500 text-xs uppercase tracking-wider mb-2 block">Your Rating</span>
              <StarRating rating={rating} onRate={setRating} interactive />
            </div>
            <Textarea
              placeholder="Write your review..."
              value={text}
              onChange={(e) => setText(e.target.value)}
              rows={4}
              className="bg-gg-black border-white/10 text-white placeholder:text-zinc-600 rounded-lg resize-none"
              data-testid="review-text-input"
            />
            <Button
              type="submit"
              disabled={submitting}
              className="bg-gg-red hover:bg-gg-red-hover text-white rounded-lg font-heading uppercase tracking-widest"
              data-testid="review-submit-button"
            >
              <Send size={16} className="mr-2" />
              {submitting ? "Submitting..." : "Submit Review"}
            </Button>
          </form>
        </motion.div>
      </div>
    </section>
  );
}
